import { UseQueryOptions, skipToken, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { templateInterpolation } from '../Helpers/templateInterpolation';
import {
    type BasePanel,
    Context,
    Params,
    RESTDataSource,
    StaticDataSource
} from '../types';
import { transformers } from './transformers';

/** We refetch the data every 60 seconds */
const defaultRefetchInterval = 60 * 1000;

/** We consider the data stale after 60 seconds */
const defaultStaleTime = 60 * 1000;

/**
 * Interpolate the params replacing the variables with the values from the context.
 * @param params - The parameters of the API.
 * @param context - The context of the API. If not provided, the context will be empty.
 * @returns The interpolated parameters.
 */
function interpolateParams(params: Params, context: Context): Params {
    if (!params) return {};
    return Object.fromEntries(
        Object.entries(params).map(([key, value]) => [
            key,
            templateInterpolation(value, context.variables)
        ])
    );
}

/**
 * Get the data from the REST API.
 */
async function getRestData(
    {
        url,
        method,
        params,
        transformer
    }: Pick<RESTDataSource, 'url' | 'method' | 'params' | 'transformer'>,
    signal: AbortSignal
) {
    // Interpolate the params replacing the variables with the values from the context

    const response = await axios(url, { method, params, signal });

    const { data } = response.data;

    if (transformer) {
        if (!(transformer in transformers)) {
            throw new Error(`Transformer ${transformer} not found`);
        }

        // eslint-disable-next-line import/namespace
        return transformers[transformer as keyof typeof transformers]({ data });
    }

    // sleep for a second to simulate a slow request
    // TODO: Remove this once we finish working on the dashboard
    // await new Promise((resolve) => {
    //     setTimeout(resolve, 2000);
    // });

    return data;
}

/**
 * Get the data from the static source.
 */
async function getStaticData(data: StaticDataSource) {
    return data.values;
}

/**
 * Use the React Query hook to fetch the data from the REST API or the static source.
 * @param panel - The panel to fetch the data from.
 * @param context - The context of the panel.
 *
 * @returns The data from the REST API or the static source.
 */
export function useReactQuery(
    panel: BasePanel,
    context: Context,
    refreshDashboard: number | null
) {
    const queryOptions: Partial<UseQueryOptions> = {};

    if (!panel.data) {
        queryOptions.queryFn = skipToken;
    } else {
        switch (panel.data.source) {
            case 'rest': {
                const {
                    method,
                    params: rawParams = {},
                    requiredParams,
                    transformer,
                    url: rawUrl
                } = panel.data as RESTDataSource;

                // Interpolate the params replacing the variables with the values from the context
                const params = interpolateParams(rawParams, context);
                const url = templateInterpolation(rawUrl, context.variables);

                // Check required params
                const hasAllRequired = requiredParams?.every(
                    (key) => params![key] !== undefined && params![key] !== ''
                );

                queryOptions.queryKey = [method, url, params, transformer];
                queryOptions.queryFn = async ({ signal }) =>
                    getRestData({ method, params, transformer, url }, signal);

                // If the panel has no data, don't fetch it
                queryOptions.enabled = hasAllRequired;
                break;
            }
            case 'static':
                queryOptions.queryFn = async () =>
                    getStaticData(panel.data as StaticDataSource);
                break;
            default:
                throw new Error('Invalid data source');
        }
    }

    if (refreshDashboard) {
        if (typeof refreshDashboard === 'number') {
            queryOptions.refetchInterval = refreshDashboard * 1000;
            queryOptions.staleTime = refreshDashboard * 1000;
        }

        if (refreshDashboard === null) {
            queryOptions.refetchInterval = false;
            queryOptions.staleTime = Infinity;
        }
    }

    const query = useQuery({
        // If no queryKey is provided, use the panel id
        queryKey: queryOptions.queryKey ?? [panel.id],
        refetchInterval: defaultRefetchInterval,
        staleTime: defaultStaleTime,
        ...queryOptions
    });

    return query;
}
